/**
 * /explore — toutes les bonnes affaires, tous les filtres (2026-09-14).
 *
 * La même grille que l'accueil, en plus complet : tous les filtres, le tri,
 * la pagination complète. Elle remplace deux pages : l'ancienne page de vente
 * de /explore, et /listings, qui redirige ici en gardant ses paramètres.
 *
 * Une seule liste pour tout le monde ; c'est la connexion qui décide de ce que
 * porte la carte (côté serveur) : le lien et la source pour un compte. La vue
 * « Marge à la revente » est retirée le 2026-10-22 (marge servie à personne) ;
 * un ancien lien ?vue=revente retombe sur les bonnes affaires.
 *
 * Les critères vivent dans l'URL : une recherche se partage et survit au
 * bouton « retour ».
 */
import React, { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Navbar from '../components/Layout/Navbar';
import FiltresAffaires from '../components/Affaires/FiltresAffaires';
import GrilleAffaires from '../components/Affaires/GrilleAffaires';
import Pagination from '../components/Affaires/Pagination';
import useAffaires from '../components/Affaires/useAffaires';
import { ecrireCriteres, lireCriteres } from '../components/Affaires/criteres';
import { nombre } from '../components/Affaires/format';
import '../components/Affaires/Affaires.css';

const PAR_PAGE = 24;

const ANNEES = [2022, 2020, 2018, 2015, 2010].map((a) => ({ v: String(a), l: `${a} et plus récent`, court: `Depuis ${a}` }));
const KMS = [30000, 60000, 100000, 150000].map((k) => ({
  v: String(k), l: `Moins de ${nombre(k)} km`, court: `< ${nombre(k)} km`,
}));
const ECARTS = [
  { v: '10', l: '10 % et plus sous la cote', court: '≥ 10 %' },
  { v: '15', l: '15 % et plus sous la cote', court: '≥ 15 %' },
  { v: '20', l: '20 % et plus sous la cote', court: '≥ 20 %' },
];
const TRIS_AFFAIRES = [
  { v: 'ecart', l: 'Plus gros écart (en %)', court: 'Plus gros écart' },
  { v: 'prix', l: 'Prix croissant', court: 'Prix croissant' },
  { v: 'km', l: 'Kilométrage croissant', court: 'Moins de km' },
  { v: 'recent', l: 'Vues le plus récemment', court: 'Plus récentes' },
];

export default function ExplorePage() {
  const location = useLocation();
  const navigate = useNavigate();
  const criteres = useMemo(() => {
    const { vue, ...c } = lireCriteres(location.search);
    return c.tri === 'marge' ? { ...c, tri: '' } : c;
  }, [location.search]);
  const { donnees, chargement, erreur } = useAffaires({ ...criteres, par_page: PAR_PAGE });

  function changer(suivant) {
    const recherche = ecrireCriteres(suivant);
    navigate({ search: recherche ? `?${recherche}` : '' });
  }

  function allerPage(n) {
    changer({ ...criteres, page: n > 1 ? String(n) : '' });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  const dims = [
    { cle: 'budget', titre: 'Budget' },
    { cle: 'departement', titre: 'Département' },
    { cle: 'marque', titre: 'Marque' },
    { cle: 'modele', titre: 'Modèle', siMarque: true },
    { cle: 'energie', titre: 'Énergie' },
    { cle: 'boite', titre: 'Boîte' },
    { cle: 'annee_min', titre: 'Année', options: ANNEES },
    { cle: 'km_max', titre: 'Kilométrage', options: KMS },
    { cle: 'ecart_min', titre: 'Écart', options: ECARTS, defaut: '10' },
    { cle: 'tri', titre: 'Tri', options: TRIS_AFFAIRES, defaut: 'ecart' },
  ];

  return (
    <>
      <Navbar />
      <main className="zk-vit">
        <section className="zk-vit-tete">
          <h1>Toutes les bonnes affaires, en ce moment</h1>
          <p>
            {donnees && (
              <><b>{nombre(donnees.total)}</b> voitures à au moins {Math.round(Math.max(donnees.seuil_pct, Number(criteres.ecart_min) || 0))} % sous leur cote, sur {nombre(donnees.annonces_comparees)} annonces comparées.</>
            )}
          </p>
        </section>

        {criteres.q && (
          <p className="zk-exp-q">
            Recherche : <b>« {criteres.q} »</b>
            <button type="button" onClick={() => changer({ ...criteres, q: '', page: '' })}>Effacer</button>
          </p>
        )}

        <FiltresAffaires
          dims={dims}
          facettes={donnees?.facettes}
          criteres={criteres}
          onChange={changer}
          total={donnees?.total}
        />

        <GrilleAffaires
          donnees={donnees}
          chargement={chargement}
          erreur={erreur}
          onEffacer={() => changer({})}
        />

        {donnees && <Pagination page={donnees.page} pages={donnees.pages} onPage={allerPage} />}

        <section className="zk-vit-methode">
          <p>
            Chaque annonce est comparée aux voitures identiques en vente en France — même modèle,
            même âge, même kilométrage. Une bonne affaire est au moins 10 % sous cette cote.
            Aucune cote extérieure : l’écart affiché est celui du marché réel.
          </p>
        </section>
      </main>
    </>
  );
}
